@file:Suppress("UnsafeCastFromDynamic")

package org.example.project.screens_sys

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSelectionMode
import org.example.project.enginetabular.TabularSideSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.SystemBuildApi
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.TouchEvent

private data class BuildRecord(
    val name: String = "",
    val version: String = "",
    val built: Boolean = true,
    val liveReleased: Boolean = false,
    val active: Boolean = false,
    val activeSys: Boolean = false,
    val createdAm: String = "",
    val createdUm: String = "",
    val createdBy: String = "",
    val activatedAm: String = "",
    val activatedUm: String = "",
    val activatedBy: String = "",
    val updatedAm: String = "",
    val updatedUm: String = "",
    val updatedBy: String = ""
)

private data class SystemBuildStatus(
    val zustand: Int = ZustandManager.Build.BEREIT,
    val action: String = "",
    val version: String = "",
    val error: String = "",
    val log: String = "",
    val startedAt: String = "",
    val processAlive: Boolean = false,
    val liveVersion: String = "",
    val builds: List<BuildRecord> = emptyList(),
    val liveBuilds: List<BuildRecord> = emptyList(),
    val journal: List<BuildJournalEntry> = emptyList()
)

private data class BuildInfoRow(val id: String, val date: String, val version: String, val type: String, val user: String, val value: String, val isNote: Boolean = false)
private data class BuildDetails(val version: String, val rows: List<BuildInfoRow>)
private data class BuildJournalEntry(val version: String, val action: String, val date: String, val time: String, val actor: String)

private object BuildScreenStyles : StyleSheet() {
    val screenRoot by style {
        width(100.percent); padding(18.px); boxSizing("border-box")
    }
    val content by style {
        width(100.percent)
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px)
    }
    val title by style { color(DashboardTheme.TEXT_LIGHT); fontSize(24.px); fontWeight("750"); textAlign("center") }
    val flow by style { color(DashboardTheme.TEXT_MUTED); fontSize(14.px); textAlign("center") }
    val sectionTitle by style { color(DashboardTheme.TEXT_LIGHT); fontSize(17.px); fontWeight("700") }
    val actionRow by style {
        display(DisplayStyle.Grid); gridTemplateColumns("minmax(220px, 1fr) minmax(220px, 1fr)")
        gap(12.px); alignItems(AlignItems.End)
    }
    val message by style { color(DashboardTheme.TEXT_MUTED); fontSize(13.px) }
    val zustandLine by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
        fontSize(13.px); fontWeight("650")
    }
    val zustandDot by style { width(9.px); height(9.px); borderRadius(50.percent) }
    val activityTime by style { property("font-variant-numeric", "tabular-nums"); minWidth(52.px) }
    val activityDots by style { display(DisplayStyle.InlineBlock); width(24.px); whiteSpace("pre") }
    val error by style { color(Color(ZustandManager.Build.colorHexFor(ZustandManager.Build.BUILD_FEHLGESCHLAGEN))); fontSize(13.px) }
    val console by style {
        flex(1); minHeight(0.px); minWidth(0.px); padding(14.px); overflow("auto"); whiteSpace("pre-wrap")
        borderRadius(8.px); backgroundColor(Color.transparent); color(Color("#b9f6ca"))
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        property("box-sizing", "border-box")
        property("background-clip", "padding-box")
        property("font-family", "Consolas, 'Courier New', monospace"); fontSize(12.px)
    }
    val consoleIdle by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
    }
    val monitorGroup by style { width(100.percent) }
    val monitorPanel by style {
        minWidth(0.px); flexShrink(0); overflow("hidden")
    }
    val monitorColumn by style {
        flex(1); minWidth(0.px); height(100.percent); display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column); gap(8.px); overflow("hidden")
    }
    val journalColumn by style {
        width(100.percent); minWidth(0.px); height(100.percent); minHeight(0.px); overflow("hidden")
    }
    val viewDivider by style {
        height(8.px); margin(12.px, 0.px, 4.px, 0.px); flexShrink(0); cursor("row-resize")
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        property("border-top", "1px solid rgba(255,255,255,0.08)")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)"); property("touch-action", "none"); property("user-select", "none")
    }
    val viewDividerHandle by style {
        height(3.px); width(42.px); borderRadius(999.px); backgroundColor(Color("rgba(255,255,255,0.92)")); property("box-shadow", "0 0 8px rgba(255,255,255,0.18)")
    }
    val lowerScroll by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(22.px); paddingBottom(18.px)
    }
    val tablesWorkspace by style {
        width(100.percent); height(680.px); minHeight(520.px); minWidth(0.px)
    }
    val pageButtons by style {
        width(100.percent); display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(2, minmax(180px, 1fr))"); gap(10.px)
    }
    val tablePane by style {
        width(100.percent); height(100.percent); minHeight(0.px); minWidth(0.px)
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); overflow("hidden")
    }
    val tablePaneBody by style {
        flex(1); minHeight(0.px); minWidth(0.px); overflow("hidden")
    }
    val detailTable by style { flex(1); minHeight(0.px); width(100.percent); overflow("hidden") }
    val chatInputBar by style {
        height(48.px); flexShrink(0); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
        padding(5.px, 8.px, 5.px, 12.px); boxSizing("border-box"); borderRadius(10.px)
        backgroundColor(Color.transparent); property("border", "1px solid rgba(255,255,255,0.14)")
    }
    val chatInput by style {
        flex(1); minWidth(0.px); height(36.px); padding(0.px, 6.px); boxSizing("border-box")
        property("border", "none"); property("outline", "none"); backgroundColor(Color.transparent)
        color(Color.white); fontSize(13.px); fontFamily("inherit")
    }
    val chatSend by style {
        width(40.px); height(36.px); flexShrink(0); display(DisplayStyle.Flex)
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
    }
}

private suspend fun loadBuildStatus(): SystemBuildStatus {
    val records = SystemBuildApi.load()
    val job = records.firstOrNull { it.buildDatensatzTyp == "JOB" } ?: All(buildDatensatzTyp = "JOB", buildZustand = ZustandManager.Build.BEREIT)
    fun mapBuild(item: All) = BuildRecord(
        name = item.id, version = item.buildVersion,
        liveReleased = item.buildLiveFreigegeben, active = item.buildAktiv, activeSys = item.buildAktiv,
        createdAm = item.erstelltAm, createdUm = item.erstelltUm, createdBy = item.erstelltVon,
        activatedAm = item.bearbeitetAm, activatedUm = item.bearbeitetUm, activatedBy = item.bearbeitetVon,
        updatedAm = item.geaendertAm, updatedUm = item.geaendertUm, updatedBy = item.geaendertVon
    )
    val buildRecords = records.filter { it.buildDatensatzTyp == "BUILD" && it.buildUmgebung == "build" }.map(::mapBuild)
    val liveRecords = records.filter { it.buildDatensatzTyp == "BUILD" && it.buildUmgebung == "live" }.map(::mapBuild)
    val journal = records.filter { it.buildDatensatzTyp == "JOURNAL" }.map {
        BuildJournalEntry(it.buildVersion, it.buildEreignisTyp, it.erstelltAm, it.erstelltUm, it.erstelltVon)
    }
    return SystemBuildStatus(
        zustand = job.buildZustand, action = job.buildAktion, version = job.buildVersion, error = job.error,
        log = job.buildWert, startedAt = job.createdAt, processAlive = ZustandManager.Build.isRunning(job.buildZustand),
        liveVersion = liveRecords.firstOrNull { it.active }?.name.orEmpty(),
        builds = buildRecords, liveBuilds = liveRecords, journal = journal
    )
}

private suspend fun postBuildAction(action: String, version: String, liveReleased: Boolean = false, nodeIds: Set<String> = emptySet()): String {
    val clean = version.removePrefix("build_").filter { it.isDigit() || it == '.' }
    val data = All(buildAktion = action, buildVersion = clean, buildLiveFreigegeben = liveReleased, buildNodeIds = nodeIds.sorted())
    return if (SystemBuildApi.execute(action, data)) "" else "Aktion fehlgeschlagen."
}

private suspend fun loadBuildDetails(version: String): BuildDetails {
    val rows = SystemBuildApi.details(version)
    val meta = rows.firstOrNull { it.buildDatensatzTyp == "DETAIL_META" }
        ?: return BuildDetails(version.removePrefix("build_"), emptyList())
    val infoRows = buildList {
        add(BuildInfoRow("META", "${meta.erstelltAm} ${meta.erstelltUm}".trim(), meta.buildVersion, "Build erzeugt", meta.erstelltVon.ifBlank { "Entwickler" }, meta.buildVorversion.takeIf { it.isNotBlank() }?.let { "Basis V $it" } ?: "Erster Build"))
        rows.filter { it.buildDatensatzTyp == "DETAIL_CHANGE" }.forEach {
            add(BuildInfoRow(it.id, "${it.erstelltAm} ${it.erstelltUm}".trim(), it.buildVersion, it.buildEreignisTyp, it.erstelltVon.ifBlank { "Entwickler" }, it.buildWert))
        }
        rows.filter { it.buildDatensatzTyp == "DETAIL_NOTE" }.forEach {
            add(BuildInfoRow(it.id, "${it.erstelltAm} ${it.erstelltUm}".trim(), it.buildVersion, it.buildEreignisTyp.ifBlank { "Notiz" }, it.erstelltVon.ifBlank { "System" }, it.buildKommentar, isNote = true))
        }
    }
    return BuildDetails(meta.buildVersion, infoRows)
}

private fun nextVersion(records: List<BuildRecord>): String {
    val latest = records.mapNotNull { it.version.split('.').mapNotNull(String::toIntOrNull).takeIf { p -> p.size == 3 } }
        .maxWithOrNull(compareBy<List<Int>> { it[0] }.thenBy { it[1] }.thenBy { it[2] }) ?: listOf(1, 0, -1)
    return "${latest[0]}.${latest[1]}.${latest[2] + 1}"
}

private fun elapsedText(startedAt: String, nowMillis: Double): String {
    val started = js("Date.parse(startedAt)") as Double
    if (started.isNaN()) return "00:00"
    val total = ((nowMillis - started) / 1000.0).toInt().coerceAtLeast(0)
    val hours = total / 3600
    val minutes = (total % 3600) / 60
    val seconds = total % 60
    return if (hours > 0) "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
    else "${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
}

private fun mark(value: Boolean) = if (value) "✅" else "⬜"

@Composable
fun SysSystemBuildScreen() {
    Style(BuildScreenStyles)
    val scope = rememberCoroutineScope()
    var state by remember { mutableStateOf(SystemBuildStatus()) }
    var message by remember { mutableStateOf("") }
    var refreshKey by remember { mutableStateOf(0) }
    var buildDataRevision by remember { mutableStateOf(0) }
    var rolloutBuild by remember { mutableStateOf<String?>(null) }
    var rolloutNodes by remember { mutableStateOf<List<All>>(emptyList()) }
    var rolloutSelection by remember { mutableStateOf<Set<String>>(emptySet()) }
    var buildDetails by remember { mutableStateOf<BuildDetails?>(null) }
    var detailComment by remember { mutableStateOf("") }
    var inventorySelection by remember { mutableStateOf<Set<String>>(emptySet()) }
    var rolloutInfoSelection by remember { mutableStateOf<Set<String>>(emptySet()) }
    var activeBuildPage by remember {
        mutableStateOf(window.localStorage.getItem("system.build.page")?.takeIf { it in setOf("inventory", "live") } ?: "inventory")
    }
    var monitorHeightPx by remember {
        mutableStateOf(window.localStorage.getItem("system.build.monitor.height")?.toIntOrNull()?.coerceIn(260, 680) ?: 360)
    }
    var monitorResizeDragging by remember { mutableStateOf(false) }
    var monitorResizePointerY by remember { mutableStateOf(0.0) }
    var activityTick by remember { mutableStateOf(0) }
    var nowMillis by remember { mutableStateOf(js("Date.now()") as Double) }
    val busy = ZustandManager.Build.isRunning(state.zustand)

    if (monitorResizeDragging) {
        DisposableEffect(Unit) {
            val moveHandler: (Event) -> Unit = { event ->
                val pointerY = (event as? MouseEvent)?.clientY?.toDouble()
                    ?: (event as? TouchEvent)?.touches?.item(0)?.clientY?.toDouble()
                if (pointerY != null) {
                    event.preventDefault()
                    monitorHeightPx = (monitorHeightPx + (pointerY - monitorResizePointerY).toInt()).coerceIn(260, 680)
                    monitorResizePointerY = pointerY
                    window.localStorage.setItem("system.build.monitor.height", monitorHeightPx.toString())
                }
            }
            val upHandler: (Event) -> Unit = { monitorResizeDragging = false }
            window.addEventListener("mousemove", moveHandler)
            window.addEventListener("mouseup", upHandler)
            window.addEventListener("touchmove", moveHandler)
            window.addEventListener("touchend", upHandler)
            window.addEventListener("touchcancel", upHandler)
            onDispose {
                window.removeEventListener("mousemove", moveHandler)
                window.removeEventListener("mouseup", upHandler)
                window.removeEventListener("touchmove", moveHandler)
                window.removeEventListener("touchend", upHandler)
                window.removeEventListener("touchcancel", upHandler)
            }
        }
    }

    LaunchedEffect(busy, state.startedAt) {
        while (busy) {
            nowMillis = js("Date.now()") as Double
            activityTick = (activityTick + 1) % 4
            delay(500)
        }
    }

    LaunchedEffect(refreshKey) {
        var observedRunningJob = false
        do {
            state = runCatching { loadBuildStatus() }.getOrElse { SystemBuildStatus(zustand = ZustandManager.Build.BUILD_FEHLGESCHLAGEN, error = it.message.orEmpty()) }
            val runningNow = ZustandManager.Build.isRunning(state.zustand)
            if (runningNow) {
                observedRunningJob = true
                delay(1000)
            }
        } while (runningNow)
        if (observedRunningJob) {
            delay(500)
            state = runCatching { loadBuildStatus() }.getOrElse { state }
            buildDataRevision++
        }
    }

    val inventoryHeaders = listOf("Version", "Zustand", "Für LIVE freigeben", "Erstellt am", "Erstellt um", "Erstellt von", "Geändert am", "Geändert um", "Geändert von")
    val inventoryRows = state.builds.map { build ->
        build.name to listOf(build.version, ZustandManager.Build.textFor(ZustandManager.Build.BUILD_ABGESCHLOSSEN), mark(build.liveReleased), build.createdAm.ifBlank { "–" }, build.createdUm.ifBlank { "–" }, build.createdBy.ifBlank { "–" }, build.updatedAm.ifBlank { "–" }, build.updatedUm.ifBlank { "–" }, build.updatedBy.ifBlank { "–" })
    }
    val rolloutHeaders = listOf("Version", "Zustand", "Erstellt am", "Erstellt um", "Erstellt von", "Freigegeben am", "Freigegeben um", "Freigegeben von")
    val rolloutRows = state.builds.filter { it.liveReleased }.map { build ->
        build.name to listOf(build.version, ZustandManager.Build.textFor(ZustandManager.Build.BEREIT), build.createdAm.ifBlank { "–" }, build.createdUm.ifBlank { "–" }, build.createdBy.ifBlank { "–" }, build.updatedAm.ifBlank { "–" }, build.updatedUm.ifBlank { "–" }, build.updatedBy.ifBlank { "–" })
    }
    val suggestedVersion = nextVersion(state.builds + state.liveBuilds)

    Div({ classes(BuildScreenStyles.screenRoot) }) {
        Div({ classes(BuildScreenStyles.content) }) {
            Div({ classes(BuildScreenStyles.monitorGroup) }) {
            Div({
                classes(BuildScreenStyles.monitorPanel)
                style { height(monitorHeightPx.px); minHeight(260.px) }
            }) {
                TabularSideSplitScreen(
                    storageId = "system-build-monitor-journal",
                    initialRatio = 0.62f,
                    minRatio = 0.30f,
                    maxRatio = 0.80f,
                    screen1 = {
                Div({ classes(BuildScreenStyles.monitorColumn) }) {
                    if (busy) Div({ classes(BuildScreenStyles.zustandLine); style { color(Color(ZustandManager.Build.colorHexFor(state.zustand))) } }) {
                        Span({ classes(BuildScreenStyles.zustandDot); style { backgroundColor(Color(ZustandManager.Build.colorHexFor(state.zustand))) } }) {}
                        Span({ classes(BuildScreenStyles.activityTime) }) { Text(elapsedText(state.startedAt, nowMillis)) }
                        Span { Text(ZustandManager.Build.textFor(state.zustand)) }
                        Span({ classes(BuildScreenStyles.activityDots) }) { Text(".".repeat(activityTick)) }
                    }
                    Div({
                        classes(BuildScreenStyles.console)
                        if (!busy) classes(BuildScreenStyles.consoleIdle)
                    }) {
                        if (!busy) {
                            Div({ style { width(320.px); maxWidth(80.percent) } }) {
                                LumaticaButton(
                                    label = "Build generieren",
                                    onClick = {
                                        scope.launch {
                                            message = postBuildAction("build", suggestedVersion)
                                            refreshKey++
                                        }
                                    },
                                    variant = LumaticaButtonVariant.Action,
                                    enabled = true,
                                    heightPx = 54,
                                    fillWidth = true
                                )
                            }
                        } else Text(state.log.ifBlank { ZustandManager.Build.textFor(state.zustand) })
                    }
                }
                    },
                    screen2 = {
                Div({ classes(BuildScreenStyles.journalColumn) }) {
                    Div({ classes(BuildScreenStyles.tablePane) }) {
                    Div({ classes(BuildScreenStyles.tablePaneBody) }) {
                    key("system-build-journal-$buildDataRevision") {
                    TabularScreen(
                        screenKey = "SystemBuild_Journal", rollenKey = "", title = "Journal-Auskunft",
                        headers = listOf("Datum", "Uhrzeit", "Typ", "Build", "Benutzer"),
                        data = state.journal.mapIndexed { index, entry ->
                            "journal_$index" to listOf(entry.date, entry.time, entry.action, entry.version, entry.actor)
                        },
                        onCommit = {}, editable = false, hardReadOnly = true,
                        selectionMode = TabularSelectionMode.ROWS,
                        nonEditableCols = setOf("Datum", "Uhrzeit", "Typ", "Build", "Benutzer"),
                        allowCreate = false, allowSave = false, allowDelete = false,
                        allowImportButton = false, allowExportButton = true,
                        allowSettingsButton = false, allowRefreshButton = false,
                        showToolbarExtras = false, showFilterRow = true, includeMetadata = false
                    )
                    }
                    }
                    }
                }
                    }
                )
            }

            Div({
                classes(BuildScreenStyles.viewDivider)
                onMouseDown {
                    monitorResizePointerY = it.clientY.toDouble()
                    monitorResizeDragging = true
                    it.preventDefault()
                }
                onTouchStart {
                    monitorResizePointerY = it.touches.item(0)?.clientY?.toDouble() ?: 0.0
                    monitorResizeDragging = true
                    it.preventDefault()
                }
            }) {
                Div({ classes(BuildScreenStyles.viewDividerHandle) })
            }
            }

            Div({ classes(BuildScreenStyles.lowerScroll) }) {
            Div({ classes(BuildScreenStyles.pageButtons) }) {
                fun selectPage(page: String) {
                    activeBuildPage = page
                    window.localStorage.setItem("system.build.page", page)
                }
                LumaticaButton(
                    label = "Build-Inventar", onClick = { selectPage("inventory") },
                    variant = if (activeBuildPage == "inventory") LumaticaButtonVariant.Action else LumaticaButtonVariant.Standard,
                    heightPx = 48, fillWidth = true
                )
                LumaticaButton(
                    label = "Live-Rollout", onClick = { selectPage("live") },
                    variant = if (activeBuildPage == "live") LumaticaButtonVariant.Action else LumaticaButtonVariant.Standard,
                    heightPx = 48, fillWidth = true
                )
            }
            Div({ classes(BuildScreenStyles.tablesWorkspace) }) {
                when (activeBuildPage) {
                    "inventory" -> {
            Div({ classes(BuildScreenStyles.tablePane) }) {
            Div({ classes(BuildScreenStyles.tablePaneBody) }) {
            key("system-build-inventory-$buildDataRevision") {
            TabularScreen(
                screenKey = "SystemBuild_Inventar", rollenKey = "", title = "2. Build-Inventar",
                headers = inventoryHeaders, data = inventoryRows, editable = true, includeMetadata = false, smartMode = false,
                toggleColumns = setOf("Für LIVE freigeben"),
                cellEditable = { _, column, _ -> column == "Für LIVE freigeben" },
                nonEditableCols = inventoryHeaders.filterNot { it == "Für LIVE freigeben" }.toSet(),
                allowCreate = false, allowSave = false, allowDelete = true,
                allowImportButton = false, allowExportButton = true,
                exportFileName = "lumatica-build-inventar.csv", onRefresh = { refreshKey++ },
                onSelectedRowsChange = { inventorySelection = it },
                extraNavButtons = listOf(
                    TabularToolbarButton("Information", "images/info.svg", enabled = inventorySelection.size == 1, onClick = {
                        inventorySelection.singleOrNull()?.let { rowId -> scope.launch {
                            detailComment = ""
                            buildDetails = loadBuildDetails(rowId)
                        } }
                    })
                ),
                onCellChanged = { rowId, column, value ->
                    if (column == "Für LIVE freigeben") {
                        val checked = value == "✅"
                        state = state.copy(builds = state.builds.map {
                            if (it.name != rowId) it else it.copy(liveReleased = checked)
                        })
                        scope.launch {
                            message = postBuildAction("set-live-release", rowId, liveReleased = checked)
                            state = loadBuildStatus()
                            buildDataRevision++
                        }
                    }
                },
                onCommit = {},
                onDeleteRows = { selected -> selected.forEach { postBuildAction("discard-build", it) }; refreshKey++ },
                createRow = { emptyList() }
            )
            }
            }
            }
                    }
                    else -> {
            Div({ classes(BuildScreenStyles.tablePane) }) {
            Div({ classes(BuildScreenStyles.tablePaneBody) }) {
            TabularScreen(
                screenKey = "SystemBuild_RolloutBuilds", rollenKey = "", title = "4. Live-Rollout",
                headers = rolloutHeaders, data = rolloutRows, editable = false, hardReadOnly = false, includeMetadata = false, smartMode = false,
                nonEditableCols = rolloutHeaders.toSet(), allowCreate = false, allowSave = false, allowDelete = false,
                allowImportButton = false, allowExportButton = true, exportFileName = "lumatica-live-builds.csv",
                onRefresh = { refreshKey++ },
                onSelectedRowsChange = { rolloutInfoSelection = it },
                extraNavButtons = listOf(
                    TabularToolbarButton("Information", "images/info.svg", enabled = rolloutInfoSelection.size == 1, onClick = {
                        rolloutInfoSelection.singleOrNull()?.let { rowId -> scope.launch {
                            detailComment = ""
                            buildDetails = loadBuildDetails(rowId)
                        } }
                    })
                ),
                onInstallSelected = { selected ->
                    selected.singleOrNull()?.let { build ->
                        scope.launch {
                            val targetVersion = build.removePrefix("build_")
                            rolloutNodes = SystemBuildApi.liveNodes().filter { node ->
                                node.buildNodeVersion.removePrefix("build_").removePrefix("V ").trim() != targetVersion
                            }
                            rolloutSelection = rolloutNodes.map { it.buildNodeId }.toSet()
                            rolloutBuild = build
                        }
                    }
                },
                isInstallEnabled = { selected -> selected.size == 1 },
                installButtonLabel = "Einspielen",
                onCommit = {}, onDeleteRows = null, createRow = { emptyList() }
            )
            }
            }
                    }
                }
            }

            if (state.error.isNotBlank()) Div({ classes(BuildScreenStyles.error) }) { Text(state.error.lineSequence().first()) }
            if (message.isNotBlank()) Div({ classes(BuildScreenStyles.message) }) { Text(message) }
            }
        }

        val selectedDetails = buildDetails
        if (selectedDetails != null) {
            fun sendBuildComment() {
                val comment = detailComment.trim()
                if (comment.isEmpty()) {
                    message = "Bitte zuerst einen Versionshinweis eingeben."
                    return
                }
                scope.launch {
                    if (SystemBuildApi.saveComment(selectedDetails.version, comment)) {
                        detailComment = ""
                        buildDetails = loadBuildDetails(selectedDetails.version)
                        message = "Versionshinweis gespeichert."
                    } else message = "Speichern fehlgeschlagen."
                }
            }
            PopupEngineCard(
                windowKey = "build-details-${selectedDetails.version}", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
                widthPx = 900, heightPx = 640,
                initialX = ((window.innerWidth.toDouble() - 900.0) / 2.0).coerceAtLeast(0.0),
                initialY = ((window.innerHeight.toDouble() - 640.0) / 2.0).coerceAtLeast(0.0),
                onClose = { buildDetails = null }
            ) {
                Div({ style { padding(18.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); height(100.percent); boxSizing("border-box") } }) {
                    Div({ classes(BuildScreenStyles.sectionTitle) }) { Text("Build V ${selectedDetails.version}") }
                    Div({ classes(BuildScreenStyles.detailTable) }) {
                        val infoHeaders = listOf("Datum", "Version", "Typ", "User", "Wert")
                        TabularScreen(
                            screenKey = "SystemBuild_Dokumentation", rollenKey = "", title = "Build-Dokumentation",
                            headers = infoHeaders,
                            data = selectedDetails.rows.map { it.id to listOf(it.date, it.version, it.type, it.user, it.value) },
                            editable = false, hardReadOnly = false, includeMetadata = false, smartMode = false,
                            nonEditableCols = infoHeaders.toSet(), allowCreate = false, allowSave = false, allowDelete = true,
                            allowImportButton = false, allowExportButton = true, allowSettingsButton = false,
                            isDeleteEnabled = { selected -> selected.size == 1 && selectedDetails.rows.any { it.id in selected && it.isNote } },
                            onDeleteRows = { ids ->
                                val noteId = ids.single()
                                check(selectedDetails.rows.any { it.id == noteId && it.isNote }) { "Nur Versionshinweise können gelöscht werden." }
                                SystemBuildApi.deleteComment(selectedDetails.version, noteId)
                                buildDetails = loadBuildDetails(selectedDetails.version)
                            },
                            showToolbarExtras = false, onCommit = {}
                        )
                    }
                    Div({ classes(BuildScreenStyles.chatInputBar) }) {
                        Input(InputType.Text, attrs = {
                            classes(BuildScreenStyles.chatInput)
                            value(detailComment)
                            attr("placeholder", "Versionshinweis schreiben")
                            onInput { detailComment = it.value }
                            onKeyDown {
                                if ((it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") && detailComment.isNotBlank()) {
                                    it.preventDefault()
                                    sendBuildComment()
                                }
                            }
                        })
                        Div({
                            classes(BuildScreenStyles.chatSend)
                            style {
                                cursor(if (detailComment.isNotBlank()) "pointer" else "default")
                                opacity(if (detailComment.isNotBlank()) 0.92 else 0.38)
                            }
                            if (detailComment.isNotBlank()) onClick { sendBuildComment() }
                        }) {
                            Img(src = "images/send.svg", attrs = {
                                style { width(24.px); height(24.px); property("filter", "brightness(0) invert(1)") }
                            })
                        }
                    }
                }
            }
        }

        val selectedRolloutBuild = rolloutBuild
        if (selectedRolloutBuild != null) {
            PopupEngineCard(
                windowKey = "live-rollout-$selectedRolloutBuild",
                zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
                widthPx = 760,
                heightPx = 620,
                initialX = ((window.innerWidth.toDouble() - 760.0) / 2.0).coerceAtLeast(0.0),
                initialY = ((window.innerHeight.toDouble() - 620.0) / 2.0).coerceAtLeast(0.0),
                onClose = { rolloutBuild = null }
            ) {
                Div({ style { padding(18.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); height(100.percent); boxSizing("border-box") } }) {
                    Div({ classes(BuildScreenStyles.sectionTitle) }) { Text("V ${selectedRolloutBuild.removePrefix("build_")} live ausrollen") }
                    Div({ classes(BuildScreenStyles.message) }) {
                        Text(if (rolloutNodes.isEmpty()) "Alle Nodes verwenden bereits diese Version." else "Wähle die Nodes aus, die diese Version erhalten sollen.")
                    }
                    Div({ style { flexGrow(1); minHeight(0.px); overflowY("auto"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
                        rolloutNodes.forEach { node ->
                            Div({ style { display(DisplayStyle.Grid); gridTemplateColumns("44px 1fr 110px 120px"); alignItems(AlignItems.Center); gap(10.px); padding(9.px); property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}") } }) {
                                LumaticaCheckbox(checked = node.buildNodeId in rolloutSelection, onChange = { checked -> rolloutSelection = if (checked) rolloutSelection + node.buildNodeId else rolloutSelection - node.buildNodeId })
                                Span { Text(node.buildNodeFirma) }
                                Span { Text("Port ${node.buildNodePort}") }
                                Span { Text("V ${node.buildNodeVersion.ifBlank { "-" }}") }
                            }
                        }
                    }
                    LumaticaButton(
                        label = "Auf ausgewählten Nodes einspielen",
                        onClick = {
                            scope.launch {
                                message = postBuildAction("activate-live-nodes", selectedRolloutBuild, nodeIds = rolloutSelection)
                                rolloutBuild = null
                                refreshKey++
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = !busy && rolloutSelection.isNotEmpty(),
                        heightPx = 54,
                        fillWidth = true
                    )
                }
            }
        }

    }
}
